import { lazy, Suspense, useEffect, useState } from 'react';

import { Menu, montarSecoes } from './componentes/menu/Menu';
import { ProvedorDaSessao, useSessao } from './ganchos/useSessao';
import { enderecoDaRota, lerRota, type Rota } from './rotas';
import { Convite } from './telas/Convite';
import { PrecisaEntrar, TelaEntrar } from './telas/Entrar/Entrar';

/**
 * A resposta pública não paga pelo construtor nem pelas planilhas da gestão.
 * Cada superfície privada entra só na rota que a usa; React Flow, em especial,
 * deixa de aumentar o primeiro JS aberto pelo celular do convidado.
 */
const TelaAuditoria = lazy(() =>
  import('./telas/Auditoria/Auditoria').then((modulo) => ({ default: modulo.TelaAuditoria })),
);
const Construtor = lazy(() =>
  import('./telas/Construtor').then((modulo) => ({ default: modulo.Construtor })),
);
const TelaFormularios = lazy(() =>
  import('./telas/Formularios/Formularios').then((modulo) => ({ default: modulo.TelaFormularios })),
);
const TelaPessoas = lazy(() =>
  import('./telas/Pessoas/Pessoas').then((modulo) => ({ default: modulo.TelaPessoas })),
);
const TelaResposta = lazy(() =>
  import('./telas/Resposta/Resposta').then((modulo) => ({ default: modulo.TelaResposta })),
);
const TelaRespostas = lazy(() =>
  import('./telas/Respostas/Respostas').then((modulo) => ({ default: modulo.TelaRespostas })),
);

/**
 * A fronteira entre as duas superfícies.
 *
 * O convite não monta sessão nem lateral: quem responde não tem conta. A tela
 * de entrar também fica fora da casca, porque ainda não existe uma identidade
 * para o menu descrever. Todo o restante compartilha UMA leitura de sessão
 * pelo `ProvedorDaSessao`.
 */

function useRota(): Rota {
  const [rota, definir] = useState<Rota>(() => lerRota(window.location.hash));

  useEffect(() => {
    const aoTrocar = () => definir(lerRota(window.location.hash));
    window.addEventListener('hashchange', aoTrocar);
    return () => window.removeEventListener('hashchange', aoTrocar);
  }, []);

  return rota;
}

function RotaDesconhecida({ caminho }: { readonly caminho: string }) {
  return (
    <div className="vazio">
      <span className="vazio__titulo">Esse endereço não existe</span>
      <p>Nada aqui responde por “{caminho}”.</p>
      <a className="botao" href={enderecoDaRota({ nome: 'fila' })}>
        Voltar para a fila
      </a>
    </div>
  );
}

function SemPerfil({ aoSair }: { readonly aoSair: () => void }) {
  return (
    <main id="conteudo" className="entrada-de-acesso">
      <div className="vazio">
        <span className="vazio__titulo">Seu acesso não está ligado a uma organização</span>
        <p>
          A conta existe, mas não há um perfil ativo para ela nesta instalação. Peça a quem é
          proprietário para criar ou reativar seu perfil.
        </p>
        <button type="button" className="botao" onClick={aoSair}>
          Sair e usar outra conta
        </button>
      </div>
    </main>
  );
}

function ConteudoDaRota({ rota }: { rota: Rota }) {
  switch (rota.nome) {
    case 'fila':
      return <TelaFormularios />;
    case 'pessoas':
      return <TelaPessoas />;
    case 'construtor':
      return <Construtor formularioId={rota.formularioId} />;
    case 'respostas':
      return <TelaRespostas formularioId={rota.formularioId} />;
    case 'resposta':
      return <TelaResposta formularioId={rota.formularioId} respostaId={rota.respostaId} />;
    case 'auditoria':
      return <TelaAuditoria formularioId={rota.formularioId} />;
    case 'desconhecida':
      return <RotaDesconhecida caminho={rota.caminho} />;
    // As duas rotas públicas são resolvidas antes de a sessão montar.
    case 'entrar':
    case 'convite':
      return null;
  }
}

function CarregandoRota() {
  return (
    <div className="tela" aria-busy="true">
      <span className="esqueleto esqueleto--titulo" aria-hidden="true" />
      <span className="esqueleto esqueleto--linha" aria-hidden="true" />
      <span className="esqueleto esqueleto--linha" aria-hidden="true" />
      <span className="so-leitor">Abrindo a tela</span>
    </div>
  );
}

function PranchetaProtegida({ rota }: { rota: Rota }) {
  const sessao = useSessao();

  if (sessao.carregando) {
    return (
      <main id="conteudo" className="entrada-de-acesso" aria-busy="true">
        <span className="esqueleto esqueleto--titulo" aria-hidden="true" />
        <span className="esqueleto esqueleto--linha" aria-hidden="true" />
        <span className="so-leitor">Lendo sua sessão</span>
      </main>
    );
  }

  if (sessao.erro) {
    return (
      <main id="conteudo" className="entrada-de-acesso">
        <p className="aviso aviso--erro">
          <span className="aviso__rotulo">Sessão indisponível</span>
          <span className="aviso__corpo">{sessao.erro}</span>
        </p>
        <a className="botao" href={enderecoDaRota({ nome: 'entrar' })}>
          Voltar para entrar
        </a>
      </main>
    );
  }

  if (!sessao.identidade) {
    return (
      <main id="conteudo" className="entrada-de-acesso">
        <PrecisaEntrar />
      </main>
    );
  }

  if (!sessao.perfil) {
    return (
      <SemPerfil
        aoSair={() => {
          void sessao.sair();
        }}
      />
    );
  }

  return (
    <div className="casca">
      <nav aria-label="Navegação principal">
        <Menu
          secoes={montarSecoes(rota, sessao.papel, null)}
          email={sessao.identidade.email}
          papel={sessao.papel}
          aoSair={() => {
            void sessao.sair().then(() => {
              window.location.hash = enderecoDaRota({ nome: 'entrar' });
            });
          }}
        />
      </nav>
      <main id="conteudo" className="conteudo">
        <Suspense fallback={<CarregandoRota />}>
          <ConteudoDaRota rota={rota} />
        </Suspense>
      </main>
    </div>
  );
}

export function App() {
  const rota = useRota();

  if (rota.nome === 'convite') return <Convite token={rota.token} />;
  if (rota.nome === 'entrar') return <TelaEntrar />;

  return (
    <>
      <a className="pular" href="#conteudo">
        Pular para o conteúdo
      </a>
      <ProvedorDaSessao>
        <PranchetaProtegida rota={rota} />
      </ProvedorDaSessao>
    </>
  );
}
